import { useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { toast } from "react-toastify";

import { sendCoCounselInvite } from "../../../api/coCounsel";
import perfectCaseLogo from "../../../assets/logo/PClogo.svg";

import {
  formatCoCounselPhone,
  getCoCounselAccountLabel,
  getCoCounselError,
  getCoCounselInviteStatus,
} from "./coCounsel.mjs";
import "./CoCounselFirmRow.css";

export const CoCounselBranding = ({ firm, isAccepted = false }) => {
  const [failedLogo, setFailedLogo] = useState(null);
  const uploadedLogo = firm?.branding_logo || firm?.branding_collapsed_logo;
  const logo = uploadedLogo && uploadedLogo !== failedLogo ? uploadedLogo : null;
  const inactive = !isAccepted && !(firm?.has_active_account ?? firm?.account_state === "active");

  return (
    <span className="co-counsel-branding" title={getCoCounselAccountLabel(firm)}>
      {firm?.copilot_active === true && (
        <span className="co-counsel-copilot-slot">
          <img
            src="/BP_resources/images/icon/copilot-logo-icon.svg"
            width="19"
            height="19"
            alt="Copilot"
          />
        </span>
      )}
      <img
        className={`co-counsel-firm-logo ${!logo && inactive ? " co-counsel-firm-logo-inactive" : ""}`}
        src={logo || perfectCaseLogo}
        width={logo ? 32 : 80}
        height={logo ? 32 : 26}
        alt={logo ? `${firm?.office_name || "Firm"} logo` : "Perfect Case"}
        onError={logo ? () => setFailedLogo(logo) : undefined}
      />
    </span>
  );
};

const inviteLabels = {
  not_sent: "Invite not sent",
  sent: "Invite sent",
  rejected: "Invite rejected",
  accepted: "Invite accepted",
};

export default function CoCounselFirmRow({ firm: relationship, index, onInviteSent }) {
  const [sending, setSending] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteError, setInviteError] = useState("");
  const firm = relationship.firm;
  const inviteStatus = getCoCounselInviteStatus(relationship);
  const canInvite = relationship.status === "pending" && relationship.can_remove === true;
  const inviteAction = inviteStatus === "not_sent" ? "Send Invite" : "Resend Invite";

  const sendInvite = async (email = "") => {
    if (sending) return;
    setInviteError("");
    setSending(true);
    try {
      const result = await sendCoCounselInvite(
        relationship.id,
        Intl.DateTimeFormat().resolvedOptions().timeZone,
        email
      );
      setShowEmailModal(false);
      toast.success(result.message || "Invite sent.");
      await onInviteSent?.();
    } catch (error) {
      const message = getCoCounselError(error, "Unable to send the invite. Please try again.");
      if (showEmailModal) setInviteError(message);
      else toast.error(message);
    } finally {
      setSending(false);
    }
  };

  const handleInviteClick = () => {
    if (sending) return;
    setInviteEmail(firm?.email?.trim() || "");
    setInviteError("");
    setShowEmailModal(true);
  };

  return (
    <div className="co-counsel-firm-row">
      <span className="co-counsel-firm-index">{index + 1}</span>
      <CoCounselBranding firm={firm} isAccepted={inviteStatus === "accepted"} />
      <span className="co-counsel-firm-name" title={firm?.office_name}>
        {firm?.office_name || "N/A"}
      </span>
      <span className="co-counsel-invite-column">
        {(inviteStatus !== "not_sent" || !canInvite) && (
          <span className="co-counsel-firm-status" aria-live="polite">
            {relationship.status === "active" ? "Accepted" : inviteLabels[inviteStatus]}
          </span>
        )}
        {canInvite && (
          <button
            type="button"
            className="co-counsel-invite-button"
            disabled={sending}
            aria-label={`${inviteStatus === "not_sent" ? "Send" : "Resend"} invite to ${firm?.office_name || "firm"}`}
            onClick={handleInviteClick}
          >
            {sending ? "Sending…" : inviteAction}
          </button>
        )}
      </span>
      <span className="co-counsel-firm-phone">{formatCoCounselPhone(firm?.phone)}</span>
      <Modal
        show={showEmailModal}
        onHide={sending ? undefined : () => setShowEmailModal(false)}
        centered
        dialogClassName="co-counsel-invite-dialog"
        contentClassName="custom-modal-new-provider"
        aria-labelledby={`co-counsel-invite-title-${relationship.id}`}
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            sendInvite(inviteEmail.trim());
          }}
        >
          <Modal.Header className="tfbp-modal-header">
            <Modal.Title
              id={`co-counsel-invite-title-${relationship.id}`}
              className="tfbp-modal-title text-uppercase"
            >
              {inviteAction}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="co-counsel-invite-body">
            <p className="co-counsel-invite-text">
              Enter the email address to invite {firm?.office_name || "this firm"}.
            </p>
            <div className="d-flex align-items-center">
              <label
                htmlFor={`co-counsel-invite-email-${relationship.id}`}
                className="co-counsel-invite-label text-grey m-0"
              >
                Firm Email
              </label>
              <input
                id={`co-counsel-invite-email-${relationship.id}`}
                type="email"
                name="invite-email"
                autoComplete="email"
                spellCheck={false}
                placeholder="Enter Email"
                className="form-control height-25 p-0 p-l-5"
                value={inviteEmail}
                onChange={(event) => setInviteEmail(event.target.value)}
                disabled={sending}
                required
              />
            </div>
            {inviteError && (
              <div className="co-counsel-invite-error" role="alert">
                {inviteError}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer
            className="p-0 mt-0 padding-outside-btn-new-provider"
            style={{ borderTop: "none" }}
          >
            <Button
              type="button"
              variant="secondary"
              className="button-padding-footer-new-provider d-flex align-items-center justify-content-center"
              disabled={sending}
              onClick={() => setShowEmailModal(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="success"
              className="button-padding-footer-new-provider d-flex align-items-center justify-content-center"
              disabled={sending}
            >
              {sending ? "Sending…" : inviteAction}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
}
